/**
 * consentChannels.ts — WHICH channels a marketing-consent write names. The one
 * rule both patient surfaces send by: only what the person was SHOWN, and only
 * what they CHANGED.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: marketing consent (2026-09-11).
 *
 * WHY NOT THE FULL STATE (review of portal #414, 2026-09-11). The first cut sent
 * both booleans on every write, and on the then Result-screen card that went
 * wrong in one specific case: the read FAILED (so the WhatsApp box was hidden
 * and both boxes sat unticked), the person ticked email, and the body carried
 * `whatsapp: false`.
 * For a returning patient who had granted WhatsApp, the server then recorded a
 * WhatsApp WITHDRAWAL they never made, from a box they were never shown. That is
 * a false entry in the one record that has to demonstrate consent (UK GDPR
 * Art 7(1) puts the burden of proof on us). clinic-service #205 takes each
 * channel as OPTIONAL and leaves an absent one exactly as it is, so a surface
 * can say only what it knows — and this is where that is decided.
 *
 * THE RULE, per channel:
 *  1. NOT SHOWN → NOT SENT. A box the person never saw cannot carry their choice.
 *  2. PRIOR STATE KNOWN → sent only when it DIFFERS from what was read.
 *  3. PRIOR STATE UNKNOWN (the read failed or timed out) → sent only as `true`,
 *     and only when they ticked it. An unticked box on a screen we could not
 *     read is the lawful DEFAULT, not a decision, so it is never turned into a
 *     withdrawal. A tick IS a decision, whatever the server held before.
 * Nothing qualifies → `null`, and the caller sends nothing at all: there is
 * nothing to record, and clinic-service answers 400 to a save naming no channel.
 *
 * WHY THE /preferences PAGE DOES NOT USE THIS (yet). It reaches its save only
 * from `stage.kind === 'ready'`, i.e. only when the read SUCCEEDED, and a hidden
 * WhatsApp box there carries the STORED value — so it cannot manufacture a
 * withdrawal, and sending an unchanged channel is a no-op the server ignores.
 * The journey lane is different: since the one-flow move (2026-09-23) the boxes
 * render on the questionnaire's consent screen, pre-submit, and are ALWAYS
 * unticked — so whatever the server holds, the screen never showed it, and the
 * lane is grants-only. `marketing/journeyConsent.ts` therefore calls this with
 * `stored: null` even though that screen now makes a read (the WhatsApp offer
 * check, which only decides whether the WhatsApp box is SHOWN): feeding the
 * read in as the prior state would turn an unticked box over a stored grant
 * into a `false` — a withdrawal nobody made. `stored: null` is the one state
 * that cannot turn silence into a decision.
 */
import type { MarketingChannel, MarketingConsentChannels } from '../api/types';
import type { MarketingChoices } from './MarketingConsentFields';

/** Which boxes were on screen. Email always is; WhatsApp only with a UK mobile or a grant already recorded. */
type ShownChannels = Record<MarketingChannel, boolean>;

export function channelsToSend(
  choices: MarketingChoices,
  shown: ShownChannels,
  /** What the server said it holds, or `null` when the read failed and the prior state is unknown. */
  stored: MarketingChoices | null,
): MarketingConsentChannels | null {
  const decide = (channel: MarketingChannel): boolean | undefined => {
    if (!shown[channel]) return undefined;
    const wanted = choices[channel];
    if (stored === null) return wanted ? true : undefined;
    return wanted !== stored[channel] ? wanted : undefined;
  };
  const email = decide('email');
  const whatsapp = decide('whatsapp');
  if (email !== undefined) return whatsapp !== undefined ? { email, whatsapp } : { email };
  if (whatsapp !== undefined) return { whatsapp };
  return null;
}
